import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Shield, CheckCircle, Clock, FileText, MessageCircle, Phone, Mail } from 'lucide-react';

function ThankYouPage() {
  const location = useLocation();
  const navigate = useNavigate();

  const {
    dossierId = '',
    prenom = '',
    expressDelivery = false,
    whatsappUpsell = false,
    email = ''
  } = location.state || {};

  const deliveryTime = expressDelivery ? '12 à 24 heures' : '24 à 48 heures';

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-white">
        <div className="container-narrow py-3 sm:py-4 flex justify-between items-center px-4 sm:px-0">
          <div className="flex items-center gap-2 min-w-0">
            <Shield className="w-5 sm:w-6 h-5 sm:h-6 text-accent flex-shrink-0" />
            <h1 className="text-base sm:text-xl font-semibold text-primary truncate">Assistance Rédactionnelle</h1>
          </div>
          <Badge variant="outline" className="text-xs flex-shrink-0 ml-2">
            Suisse romande
          </Badge>
        </div>
      </header>

      <div className="py-8 sm:py-16">
        <div className="container-narrow max-w-2xl">

          {/* Confirmation visuelle */}
          <div className="text-center mb-8 sm:mb-10">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-100 mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>

            <h1 className="text-3xl sm:text-4xl font-semibold text-foreground mb-4" data-testid="thank-you-title">
              Merci{prenom ? `, ${prenom}` : ''} !
            </h1>

            <p className="text-lg text-muted-foreground leading-relaxed max-w-xl mx-auto">
              Votre demande a bien été enregistrée. Notre équipe spécialisée prend en charge votre dossier dès maintenant.
            </p>
          </div>

          {/* Numéro de dossier */}
          {dossierId && (
            <Card className="p-4 sm:p-5 mb-6 bg-primary/5 border-primary/20 text-center" data-testid="dossier-reference">
              <p className="text-sm text-muted-foreground mb-1">Référence de votre dossier</p>
              <p className="text-lg font-semibold text-primary font-mono tracking-wide">
                {dossierId.substring(0, 8).toUpperCase()}
              </p>
            </Card>
          )}

          {/* Les étapes à venir */}
          <Card className="p-6 sm:p-8 mb-6 border-border" data-testid="steps-card">
            <h2 className="text-xl sm:text-2xl font-semibold text-foreground mb-6">
              Ce qui va se passer maintenant
            </h2>

            <div className="space-y-6">
              {/* Étape 1 */}
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-accent" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground text-sm sm:text-base">Analyse de votre dossier</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Nos spécialistes examinent chaque détail de votre situation et les documents fournis pour construire une argumentation solide.
                  </p>
                </div>
              </div>

              {/* Étape 2 */}
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center">
                  <Shield className="w-5 h-5 text-accent" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground text-sm sm:text-base">Rédaction de votre courrier</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Votre courrier est rédigé selon les normes du droit administratif suisse (Code civil, Code des obligations) pour maximiser vos chances de succès.
                  </p>
                </div>
              </div>

              {/* Étape 3 */}
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-accent" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground text-sm sm:text-base">Vérification qualité</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Chaque courrier passe par un contrôle de qualité rigoureux pour s'assurer de sa conformité et de son efficacité.
                  </p>
                </div>
              </div>

              {/* Étape 4 */}
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground text-sm sm:text-base">Livraison par email</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Vous recevrez votre courrier finalisé en format PDF directement dans votre boîte email{email ? ` (${email})` : ''}.
                  </p>
                </div>
              </div>
            </div>
          </Card>

          {/* Délai de livraison */}
          <Card className={`p-5 sm:p-6 mb-6 ${expressDelivery ? 'bg-amber-50 border-amber-200' : 'bg-primary/5 border-primary/20'}`} data-testid="delivery-info">
            <div className="flex items-center gap-3 mb-2">
              <Clock className={`w-5 h-5 ${expressDelivery ? 'text-amber-600' : 'text-primary'}`} />
              <h3 className="font-semibold text-foreground text-sm sm:text-base">
                Délai de livraison estimé
              </h3>
            </div>
            <p className={`text-2xl sm:text-3xl font-bold ${expressDelivery ? 'text-amber-700' : 'text-accent'}`}>
              {deliveryTime}
            </p>
            {expressDelivery && (
              <p className="text-sm text-amber-700 mt-2 font-medium">
                Votre dossier bénéficie du traitement prioritaire express.
              </p>
            )}
          </Card>

          {/* WhatsApp mention */}
          {whatsappUpsell && (
            <Card className="p-5 sm:p-6 mb-6 bg-green-50 border-green-200" data-testid="whatsapp-info">
              <div className="flex items-center gap-3 mb-2">
                <MessageCircle className="w-5 h-5 text-green-600" />
                <h3 className="font-semibold text-foreground text-sm sm:text-base">Conseiller WhatsApp</h3>
              </div>
              <p className="text-sm text-green-800">
                Un conseiller vous contactera par WhatsApp dans les prochaines heures pour discuter de votre dossier et s'assurer que toutes les informations sont complètes.
              </p>
            </Card>
          )}

          {/* Message de réassurance */}
          <Card className="p-6 sm:p-8 mb-6 border-border bg-card" data-testid="reassurance-card">
            <h2 className="text-xl sm:text-2xl font-semibold text-foreground mb-4">
              Vous n'êtes plus seul
            </h2>
            <div className="space-y-4 text-sm sm:text-base text-muted-foreground leading-relaxed">
              <p>
                Face aux administrations, on se sent souvent démuni. Les formulaires compliqués, les délais serrés, le jargon juridique... 
                <span className="font-semibold text-foreground"> Tout cela, c'est désormais notre affaire.</span>
              </p>
              <p>
                Si des informations manquent ou si un point nécessite une clarification, notre équipe vous recontactera directement. 
                Aucun détail ne sera laissé au hasard.
              </p>
              <p className="font-semibold text-foreground">
                Votre situation est entre de bonnes mains.
              </p>
            </div>
          </Card>

          {/* Contact */}
          <div className="text-center space-y-3">
            <p className="text-sm text-muted-foreground">
              Une question ? Besoin d'ajouter une information ?
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a 
                href="mailto:contact@assistance-redactionnelle.ch" 
                className="inline-flex items-center gap-2 text-sm text-accent hover:text-accent/80 font-medium"
                data-testid="contact-email-link"
              >
                <Mail className="w-4 h-4" />
                contact@assistance-redactionnelle.ch
              </a>
              <span className="hidden sm:inline text-muted-foreground">·</span>
              <a 
                href="tel:+41000000000" 
                className="inline-flex items-center gap-2 text-sm text-accent hover:text-accent/80 font-medium"
                data-testid="contact-phone-link"
              >
                <Phone className="w-4 h-4" />
                +41 (0) 00 000 00 00
              </a>
            </div>
          </div>

          <Separator className="my-8" />

          {/* Retour accueil */}
          <div className="text-center">
            <Button
              variant="outline"
              onClick={() => navigate('/')}
              className="h-10 sm:h-12 px-6 text-sm"
              data-testid="btn-back-home"
            >
              Retour à l'accueil
            </Button>
          </div>

          {/* Disclaimer */}
          <p className="text-center text-xs text-muted-foreground mt-8 max-w-lg mx-auto">
            Ce service constitue une assistance rédactionnelle et ne remplace en aucun cas un conseil juridique professionnel. 
            Aucune garantie de résultat n'est donnée. Consultez un avocat pour toute question de droit.
          </p>
        </div>
      </div>
    </div>
  );
}

export default ThankYouPage;
